export type ClerkTokenGetter = () => Promise<string | null>;

let clerkTokenGetter: ClerkTokenGetter | null = null;

export const setClerkTokenGetter = (getToken: ClerkTokenGetter | null): void => {
  clerkTokenGetter = getToken;
};

export const authenticatedFetch = async (
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> => {
  if (!clerkTokenGetter) {
    throw new Error('Authentication is still initializing. Please try again.');
  }

  const token = await clerkTokenGetter();
  if (!token) {
    throw new Error('You must be signed in to access this resource.');
  }

  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);

  return fetch(input, {
    ...init,
    headers,
  });
};

// Public pages deliberately use this helper so their requests never depend on
// Clerk initialization and never accidentally receive an authorization token.
const PUBLIC_FETCH_RETRY_DELAYS_MS = [500, 1500];
const RETRYABLE_STATUS_CODES = new Set([502, 503, 504]);

const requestMethod = (
  input: RequestInfo | URL,
  init?: RequestInit
): string => (
  init?.method
  ?? (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET')
).toUpperCase();

export const publicFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  const canRetry = requestMethod(input, init) === 'GET';

  for (let attempt = 0; ; attempt += 1) {
    try {
      const response = await fetch(input, init);
      const shouldRetry = canRetry
        && RETRYABLE_STATUS_CODES.has(response.status)
        && attempt < PUBLIC_FETCH_RETRY_DELAYS_MS.length;

      if (!shouldRetry) {
        return response;
      }
    } catch (error) {
      const isAbort = error instanceof DOMException && error.name === 'AbortError';
      if (
        !canRetry
        || isAbort
        || attempt >= PUBLIC_FETCH_RETRY_DELAYS_MS.length
      ) {
        throw error;
      }
    }

    await new Promise(resolve => {
      setTimeout(resolve, PUBLIC_FETCH_RETRY_DELAYS_MS[attempt]);
    });
  }
};
